import type { 
  CompensationRule, 
  CompensationCalculationInput, 
  CompensationCalculationResult, 
  PayslipLineItemInput,
  RateContext,
  SalaryOvertimeTier,
  DailyHoursBreakdown
} from './types';
import { compensationService } from './compensationService';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { 
  calculateProgressiveTax, 
  resolveTaxableEarnings,
  toSafeNumber,
  type TaxBracketConfig,
  type TaxCalculationResult,
  type TaxableComponents
} from '../../../shared/compensation/tax';
import { devLog } from '@/utils/logger';

/**
 * Get currency symbol from currency code
 */
const getCurrencySymbol = (currencyCode: string = 'USD'): string => {
  const symbols: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'INR': '₹',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'CHF',
    'SEK': 'kr',
    'NZD': 'NZ$',
    'SGD': 'S$',
    'HKD': 'HK$',
    'NOK': 'kr',
    'KRW': '₩',
    'TRY': '₺',
    'RUB': '₽',
    'BRL': 'R$',
    'ZAR': 'R',
    'MXN': 'MX$',
    'IDR': 'Rp',
    'THB': '฿',
    'MYR': 'RM',
    'PHP': '₱',
    'VND': '₫',
  };
  
  return symbols[currencyCode] || currencyCode + ' ';
};

/**
 * Result from rate calculation with source metadata for traceability
 */
interface RateResult {
  rate: number;
  source: 'hourly_rate' | 'salary' | 'none';
  ruleId?: string;
  annualSalary?: number;
  weeklyHours?: number;
  salaryMode?: string; // 'annual' | 'monthly_fixed'
  monthlyFixedAmount?: number; // The fixed monthly salary amount when in monthly_fixed mode
  metadata?: { multiplier?: number }; // ✅ NEW: For transparency
}

/**
 * Resolve effective weekly hours from context with priority:
 * 1. Salary rule override (compensation_rules.weekly_hours) - EXPLICIT contract terms
 * 2. Employee contract hours (profiles.weekly_hours)
 * 3. Company default constant (40 hours) - NO business hours dependency
 * 
 * Also applies FTE multiplier for part-time employees.
 */
const resolveWeeklyHours = (ctx: RateContext = {}): number => {
  const baseHours = 
    ctx.salaryRuleWeeklyHours ?? // Priority 1: Explicit salary rule override
    ctx.employeeWeeklyHours ??   // Priority 2: Employee contract
    40;                          // Priority 3: Simple constant (no business hours)
  
  const fte = ctx.fte ?? 1.0;
  return Math.max(0, baseHours * fte);
};

/**
 * Resolve salary schedule from rule configuration
 * Priority: scheduled_days > workweek_days > default 5-day (Mon-Fri)
 */
const resolveSalarySchedule = (rule: CompensationRule): number[] => {
  // Priority 1: Explicit scheduled_days array
  if (rule.scheduled_days && Array.isArray(rule.scheduled_days) && rule.scheduled_days.length > 0) {
    return rule.scheduled_days;
  }
  
  // Priority 2: Derive from workweek_days (first N weekdays starting Monday)
  if (rule.workweek_days) {
    const count = Math.floor(rule.workweek_days);
    // Monday = 1, Tuesday = 2, ..., Friday = 5, Saturday = 6, Sunday = 0
    return Array.from({ length: Math.min(count, 7) }, (_, i) => (i + 1) % 7);
  }
  
  // Priority 3: Default Monday-Friday (1-5)
  return [1, 2, 3, 4, 5];
};

/**
 * Check if a day is non-scheduled based on the schedule
 */
const isNonScheduledDay = (dayIndex: number, schedule: number[]): boolean => {
  return !schedule.includes(dayIndex);
};

/**
 * Calculate salary overtime tiers for a single day
 * Uses from_hours/to_hours ranges instead of threshold_hours
 */
const calculateSalaryOvertimeTiers = (
  hours: number,
  tiers: SalaryOvertimeTier[],
  baseRate: number
): { totalAmount: number; breakdown: { label: string; hours: number; rate: number; multiplier: number; amount: number }[] } => {
  // Sort tiers by from_hours ascending
  const sortedTiers = [...tiers].sort((a, b) => a.from_hours - b.from_hours);
  
  let processedHours = 0;
  let totalAmount = 0;
  const breakdown: { label: string; hours: number; rate: number; multiplier: number; amount: number }[] = [];
  
  for (const tier of sortedTiers) {
    if (processedHours >= hours) break;
    
    // Calculate hours in this tier
    const tierStart = tier.from_hours;
    const tierEnd = tier.to_hours ?? Infinity;
    
    // Hours that fall within this tier's range
    const hoursInTier = Math.max(0, Math.min(hours, tierEnd) - Math.max(processedHours, tierStart));
    
    if (hoursInTier > 0) {
      const tierRate = baseRate * tier.multiplier;
      const tierAmount = hoursInTier * tierRate;
      
      // Generate label based on range
      const label = tier.to_hours 
        ? `${tier.from_hours}-${tier.to_hours}h at ${tier.multiplier}x`
        : `${tier.from_hours}h+ at ${tier.multiplier}x`;
      
      breakdown.push({
        label,
        hours: hoursInTier,
        rate: tierRate,
        multiplier: tier.multiplier,
        amount: tierAmount
      });
      
      totalAmount += tierAmount;
      processedHours += hoursInTier;
    }
  }
  
  return { totalAmount, breakdown };
};

export const calculationEngine = {
  /**
   * Main compensation calculation engine
   * Enhanced with allowance calculations from time entry analytics
   */
  calculateCompensation: async (
    input: CompensationCalculationInput, 
    rules: CompensationRule[],
    analytics?: any // TimeEntryAnalyticsResult from client or server
  ): Promise<CompensationCalculationResult> => {
    const lineItems: PayslipLineItemInput[] = [];
    let totalEarnings = 0;
    let totalDeductions = 0;
    let actualOvertimeHours = input.overtimeHours || 0; // Will be updated after filtering

    // Group rules by type for easier processing
    const rulesByType = rules.reduce((acc, rule) => {
      acc[rule.rule_type] = acc[rule.rule_type] || [];
      acc[rule.rule_type].push(rule);
      return acc;
    }, {} as Record<string, CompensationRule[]>);

    // Check if holiday pay is enabled
    const baseRateRule = rulesByType['hourly_rate']?.[0] || rulesByType['salary']?.[0];
    const appliesToHolidays = baseRateRule?.applies_to_holidays ?? true;

    // Only separate holiday hours if applies_to_holidays is true
    const holidayHoursToSubtract = appliesToHolidays ? (input.holidayHours || 0) : 0;

    // Calculate regular hours (subtract overtime AND holiday hours if applicable)
    // CHANGED: Use 'let' instead of 'const' to allow adjustment during salary OT processing
    let regularHours = Math.max(0, input.hoursWorked - (input.overtimeHours || 0) - holidayHoursToSubtract);

    devLog('[Regular Hours Calc]:', {
      hoursWorked: input.hoursWorked,
      overtimeHours: input.overtimeHours,
      holidayHours: input.holidayHours,
      holidayHoursToSubtract,
      appliesToHolidays,
      regularHours,
      calculation: `${input.hoursWorked} - ${input.overtimeHours || 0} - ${holidayHoursToSubtract} = ${regularHours}`
    });
    
    // Get rates with metadata - support both hourly_rate and salary (converted to hourly)
    const regularRateResult = calculationEngine.getEffectiveRate(
      rulesByType['hourly_rate'] || [],
      rulesByType['salary'] || [],
      input.rateContext  // NEW: Pass context for contract-aware conversion
    );
    const regularRate = regularRateResult.rate;
    
    // Get overtime rate (pass regularRate for multiplier calculations)
    const overtimeRateResult = calculationEngine.getEffectiveRate(
      rulesByType['overtime'] || [], 
      [],
      {},
      regularRate // ✅ NEW: Base rate for multiplier calculation
    );
    
    // ✅ IMPROVED: No hardcoded fallback, use 1.5× only if no rule exists
    const overtimeRate = rulesByType['overtime']?.length > 0
      ? overtimeRateResult.rate
      : regularRate * 1.5; // Fallback only when no overtime rule exists
    
    // Use configurable holiday multiplier from base rate rule (default to 2.0)
    const holidayMultiplier = baseRateRule?.holiday_multiplier ?? 2.0;
    const holidayRate = regularRate * holidayMultiplier;

    const effectiveRegularRate = regularRate;

    // Process salary non-scheduled day overtime FIRST (NEW: Salary Rule Schedule Enhancement)
    // This must happen BEFORE creating the regular hours line item to prevent double-counting
    let totalNonScheduledHours = 0; // Track for summary
    const salaryRule = rulesByType['salary']?.[0];
    const currencySymbol = getCurrencySymbol(input.currencyCode);
    
    if (salaryRule && input.dailyHoursBreakdown && input.dailyHoursBreakdown.length > 0) {
      const schedule = resolveSalarySchedule(salaryRule);
      const baseRate = regularRate; // Use the already calculated base rate
      
      // Process salary OT tiers if configured (REMOVED: Blocking guard for dedicated OT rule)
      // Non-scheduled day OT can coexist with standard overtime rules
      if (salaryRule.ot_tiers && Array.isArray(salaryRule.ot_tiers) && salaryRule.ot_tiers.length > 0) {
        const otTiers = salaryRule.ot_tiers as unknown as SalaryOvertimeTier[];
        
        let displayOrderOffset = 0;
        
        for (const day of input.dailyHoursBreakdown) {
          // Only process non-scheduled days (weekends or days outside schedule)
          // Holidays trigger OT tiers ONLY when applies_to_holidays is enabled
          if (isNonScheduledDay(day.dayIndex, schedule) || (day.isHoliday && appliesToHolidays)) {
            const otResult = calculateSalaryOvertimeTiers(day.hours, otTiers, baseRate);
            totalNonScheduledHours += day.hours; // Track for summary
            
            // CRITICAL: Subtract these hours from regularHours BEFORE creating regular hours line item
            regularHours = Math.max(0, regularHours - day.hours);
            
            // Add line items for each tier
            otResult.breakdown.forEach((tier, idx) => {
              totalEarnings += tier.amount;
              
              lineItems.push({
                line_item_type: 'overtime',
                calculation_method: 'multiplier',
                description: `Salary Non-Scheduled Day OT - ${tier.label} (${day.date}, ${tier.hours.toFixed(2)} hrs @ ${currencySymbol}${tier.rate.toFixed(2)}/hr - ${tier.multiplier}x)`,
                category: 'earnings',
                hours_applied: tier.hours,
                rate_applied: tier.rate,
                base_amount: baseRate,
                gross_amount: tier.amount,
                net_amount: tier.amount,
                is_taxable: true,
                compensation_rule_id: salaryRule.id,
                calculation_notes: `Non-scheduled day overtime tier (${tier.multiplier}x multiplier)`,
                display_order: 200 + displayOrderOffset + (idx / 100)
              });
            });
            
            displayOrderOffset += 1;
          }
        }
        
        devLog(`[Calculation Engine] Processed ${totalNonScheduledHours} non-scheduled hours using salary OT tiers`);
        devLog(`[Calculation Engine] Regular hours after non-scheduled day subtraction: ${regularHours}`);
      }
    }

    // Calculate base pay (NOW with corrected regularHours that excludes non-scheduled day hours)
    // Monthly fixed salary mode: pay full fixed amount regardless of hours worked
    if (regularRateResult.salaryMode === 'monthly_fixed' && regularRateResult.monthlyFixedAmount) {
      const basePayAmount = regularRateResult.monthlyFixedAmount;
      totalEarnings += basePayAmount;
      
      const description = `Monthly Fixed Salary (${currencySymbol}${basePayAmount.toLocaleString()}, ${regularHours.toFixed(2)} hrs tracked)`;
      
      lineItems.push({
        line_item_type: 'salary',
        calculation_method: 'fixed_amount',
        description,
        category: 'earnings',
        hours_applied: regularHours, // Record hours for audit/leave-accrual purposes
        rate_applied: effectiveRegularRate, // Derived hourly rate for reference
        base_amount: basePayAmount,
        gross_amount: basePayAmount,
        net_amount: basePayAmount,
        compensation_rule_id: regularRateResult.ruleId,
        display_order: 1
      });
    } else if (regularHours > 0 && effectiveRegularRate > 0) {
      const basePayAmount = regularHours * effectiveRegularRate;
      totalEarnings += basePayAmount;
      
      // Enhanced description for salary conversions
      const description = regularRateResult.source === 'salary'
        ? `Regular Hours (${regularHours.toFixed(2)} hrs @ ${currencySymbol}${effectiveRegularRate.toFixed(2)}/hr, from ${currencySymbol}${regularRateResult.annualSalary?.toLocaleString()} salary)`
        : `Regular Hours (${regularHours.toFixed(2)} hrs @ ${currencySymbol}${effectiveRegularRate.toFixed(2)}/hr)`;
      
      lineItems.push({
        line_item_type: regularRateResult.source === 'salary' ? 'salary' : 'hourly_rate',
        calculation_method: 'fixed_amount',
        description,
        category: 'earnings',
        hours_applied: regularHours,
        rate_applied: effectiveRegularRate,
        base_amount: effectiveRegularRate,
        gross_amount: basePayAmount,
        net_amount: basePayAmount,
        compensation_rule_id: regularRateResult.ruleId,
        display_order: 1
      });
    }

    // Calculate overtime pay
    if ((input.overtimeHours || 0) > 0) {
      const overtimeRule = rulesByType['overtime']?.[0];
      
      // NEW: Filter out non-scheduled day OT if salary rule handles it (ONLY IF SALARY RULE IS ASSIGNED)
      let filteredDailyOvertimeHours = input.dailyOvertimeHours || [];
      let filteredOvertimeTotal = input.overtimeHours || 0;
      
      const salaryRule = rulesByType['salary']?.[0];
      const hasSalaryNonScheduledTiers = salaryRule?.ot_tiers && 
        Array.isArray(salaryRule.ot_tiers) && 
        salaryRule.ot_tiers.length > 0;
      
      if (hasSalaryNonScheduledTiers && input.dailyOvertimeDetails) {
        // Filter to only scheduled day overtime
        const scheduledDayOT = input.dailyOvertimeDetails.filter(d => d.isScheduledDay);
        filteredDailyOvertimeHours = scheduledDayOT.map(d => d.hours);
        filteredOvertimeTotal = scheduledDayOT.reduce((sum, d) => sum + d.hours, 0);
        
        // UPDATE: Track the actual overtime that will be paid
        actualOvertimeHours = filteredOvertimeTotal;
        
        const excludedHours = (input.overtimeHours || 0) - filteredOvertimeTotal;
        if (excludedHours > 0) {
          devLog(`[Calculation Engine] Excluded ${excludedHours.toFixed(2)} non-scheduled day OT hours from standard OT rule (handled by salary tiers)`);
        }
      }
      
      // Use filtered values for standard OT calculation
      // FIX: Check for tiered_rates more robustly (array check) and add logging
      const hasTieredRates = overtimeRule?.tiered_rates && 
                            Array.isArray(overtimeRule.tiered_rates) && 
                            overtimeRule.tiered_rates.length > 0;
      
      if (filteredOvertimeTotal > 0 && overtimeRule?.calculation_method === 'tiered_rate' && hasTieredRates) {
        // Process each day's OT separately for accurate tiering
        // FIX: Use filteredOvertimeTotal as fallback if dailyOvertimeHours not available
        if (filteredDailyOvertimeHours.length > 0) {
          const cumulativeTierBreakdown = new Map<number, {
            tier: number;
            hours: number;
            rate: number;
            amount: number;
            description: string;
          }>();
          
          // Process each day's OT independently
          filteredDailyOvertimeHours.forEach((dayOT) => {
            const dayResult = calculationEngine.calculateTieredOvertimeAmount(
              dayOT,
              overtimeRule,
              effectiveRegularRate,
              input.currencyCode
            );
            
            totalEarnings += dayResult.totalAmount;
            
            // Aggregate tier breakdowns across days
            dayResult.tierBreakdown.forEach(tier => {
              const existing = cumulativeTierBreakdown.get(tier.tier);
              if (existing) {
                existing.hours += tier.hours;
                existing.amount += tier.amount;
              } else {
                cumulativeTierBreakdown.set(tier.tier, { ...tier });
              }
            });
          });
          
          // Create line items from cumulative breakdown with refreshed descriptions
          Array.from(cumulativeTierBreakdown.values()).forEach((tier, index) => {
            // Get multiplier from the original rule configuration
            const tierConfig = (overtimeRule.tiered_rates as unknown as import('./types').OvertimeTier[])[tier.tier - 1];
            const multiplier = tierConfig?.multiplier || 1.5;
            
            // Regenerate description with aggregated hours
            const refreshedDescription = `Overtime Tier ${tier.tier} (${tier.hours.toFixed(2)} hrs @ ${currencySymbol}${tier.rate.toFixed(2)}/hr - ${multiplier}x base rate)`;
            
            lineItems.push({
              line_item_type: 'overtime',
              calculation_method: 'tiered_rate',
              description: refreshedDescription,
              category: 'earnings',
              hours_applied: tier.hours,
              rate_applied: tier.rate,
              base_amount: tier.rate,
              gross_amount: tier.amount,
              net_amount: tier.amount,
              compensation_rule_id: overtimeRule.id,
              display_order: 2 + index / 10
            });
          });
        } else {
          // Fallback: Use total overtime hours if dailyOvertimeHours not provided
          devLog(`[Calculation Engine] Using fallback for tiered overtime - total hours: ${filteredOvertimeTotal}`);
          const tieredResult = calculationEngine.calculateTieredOvertimeAmount(
            filteredOvertimeTotal,
            overtimeRule,
            effectiveRegularRate,
            input.currencyCode
          );
          
          totalEarnings += tieredResult.totalAmount;
          
          tieredResult.tierBreakdown.forEach((tier, index) => {
            lineItems.push({
              line_item_type: 'overtime',
              calculation_method: 'tiered_rate',
              description: tier.description,
              category: 'earnings',
              hours_applied: tier.hours,
              rate_applied: tier.rate,
              base_amount: tier.rate,
              gross_amount: tier.amount,
              net_amount: tier.amount,
              compensation_rule_id: overtimeRule.id,
              display_order: 2 + index / 10
            });
          });
        }
      } else if (filteredOvertimeTotal > 0 && overtimeRate > 0) {
        // Use simple overtime calculation (for non-tiered rules OR as final fallback)
        const overtimeAmount = filteredOvertimeTotal * overtimeRate;
        totalEarnings += overtimeAmount;

        const overtimeDescription = overtimeRateResult.metadata?.multiplier
          ? `Overtime Hours (${filteredOvertimeTotal.toFixed(2)} hrs @ ${overtimeRateResult.metadata.multiplier}× = ${currencySymbol}${overtimeRate.toFixed(2)}/hr)`
          : `Overtime Hours (${filteredOvertimeTotal.toFixed(2)} hrs @ ${currencySymbol}${overtimeRate.toFixed(2)}/hr)`;

        lineItems.push({
          line_item_type: 'overtime',
          calculation_method: rulesByType['overtime']?.[0]?.calculation_method || 'fixed_amount',
          description: overtimeDescription,
          category: 'earnings',
          hours_applied: filteredOvertimeTotal,
          rate_applied: overtimeRate,
          base_amount: overtimeRate,
          gross_amount: overtimeAmount,
          net_amount: overtimeAmount,
          compensation_rule_id: rulesByType['overtime']?.[0]?.id,
          display_order: 2
        });
      } else if (filteredOvertimeTotal > 0) {
        // FINAL FALLBACK: If we have overtime but no line item was created yet, 
        // create a simple one to prevent data loss
        console.warn(`[Calculation Engine] Creating emergency fallback overtime line item for ${filteredOvertimeTotal} hours`);
        const fallbackRate = regularRate * 1.5; // Standard overtime multiplier
        const overtimeAmount = filteredOvertimeTotal * fallbackRate;
        totalEarnings += overtimeAmount;

        lineItems.push({
          line_item_type: 'overtime',
          calculation_method: 'multiplier',
          description: `Overtime Hours (${filteredOvertimeTotal.toFixed(2)} hrs @ ${currencySymbol}${fallbackRate.toFixed(2)}/hr)`,
          category: 'earnings',
          hours_applied: filteredOvertimeTotal,
          rate_applied: fallbackRate,
          base_amount: fallbackRate,
          gross_amount: overtimeAmount,
          net_amount: overtimeAmount,
          compensation_rule_id: rulesByType['overtime']?.[0]?.id,
          display_order: 2
        });
      }
    }

    // Calculate holiday pay (respects "Applies to Holidays" toggle)
    // Note: appliesToHolidays already defined above at line 72
    
    if ((input.holidayHours || 0) > 0 && holidayRate > 0 && appliesToHolidays) {
      const holidayAmount = (input.holidayHours || 0) * holidayRate;
      totalEarnings += holidayAmount;

      lineItems.push({
        line_item_type: regularRateResult.source === 'salary' ? 'salary' : 'hourly_rate',
        calculation_method: 'fixed_amount',
        description: `Holiday Hours (${input.holidayHours?.toFixed(2)} hrs @ ${currencySymbol}${holidayRate.toFixed(2)}/hr - ${holidayMultiplier}x base rate)`,
        category: 'earnings',
        hours_applied: input.holidayHours,
        rate_applied: holidayRate,
        base_amount: holidayRate,
        gross_amount: holidayAmount,
        net_amount: holidayAmount,
        compensation_rule_id: baseRateRule?.id,
        display_order: 3
      });
    }

    // Calculate allowances using typed allowance engine (requires analytics data)
    // All allowance rules require allowance_type (enforced by UI validation)
    if (rulesByType['allowance'] && rulesByType['allowance'].length > 0) {
      if (analytics) {
        const { calculateAllowances } = await import('./allowanceEngine');
        const allowanceResult = calculateAllowances(analytics, rulesByType['allowance']);
        
        for (const lineItem of allowanceResult.lineItems) {
          totalEarnings += lineItem.gross_amount;
          lineItems.push(lineItem);
        }
      } else {
        // Analytics unavailable — allowances require analytics data for accurate calculation
        // Skip silently; this path is hit by single-employee calculation which doesn't provide analytics
        console.warn('[calculationEngine] Skipping allowance calculation: analytics data not provided. Allowances require time entry analytics for accurate per-day/shift/period calculations.');
      }
    }

    // Process paid leave entries BEFORE tax/deduction calculations
    // This ensures leave pay is included in totalEarnings for accurate tax and percentage deductions
    let totalPaidLeaveHours = 0;
    if (input.paidLeaveEntries && input.paidLeaveEntries.length > 0 && effectiveRegularRate > 0) {
      for (const leave of input.paidLeaveEntries) {
        if (leave.hours > 0) {
          const leaveAmount = leave.hours * effectiveRegularRate;
          totalEarnings += leaveAmount;
          totalPaidLeaveHours += leave.hours;

          lineItems.push({
            line_item_type: 'paid_leave',
            calculation_method: 'fixed_amount',
            description: `Paid Leave - ${leave.leaveType} (${leave.startDate} to ${leave.endDate})`,
            category: 'earnings',
            hours_applied: leave.hours,
            rate_applied: effectiveRegularRate,
            base_amount: effectiveRegularRate,
            gross_amount: leaveAmount,
            net_amount: leaveAmount,
            is_taxable: true,
            display_order: 5,
          });
        }
      }
      devLog(`[Calculation Engine] Processed ${totalPaidLeaveHours} paid leave hours at rate ${effectiveRegularRate}, added ${totalPaidLeaveHours * effectiveRegularRate} to totalEarnings`);
    }

    // Calculate taxable components by summing ACTUAL line items (not intermediate values)
    // This ensures tiered overtime and all earnings are correctly included
    // CRITICAL: Service charges must be excluded from taxable earnings
    const taxableComponents: TaxableComponents = {
      regular: lineItems
        .filter(item => (item.line_item_type === 'salary' || item.line_item_type === 'hourly_rate' || item.line_item_type === 'paid_leave') && item.category === 'earnings')
        .filter(item => !item.description?.includes('Holiday')) // Exclude holiday from regular
        .filter(item => !item.description?.includes('Service Charge')) // Exclude service charges from taxable income
        .reduce((sum, item) => sum + item.gross_amount, 0),
      overtime: lineItems
        .filter(item => item.line_item_type === 'overtime')
        .filter(item => !item.description?.includes('Service Charge')) // Exclude service charges from taxable income
        .reduce((sum, item) => sum + item.gross_amount, 0),
      holiday: lineItems
        .filter(item => 
          (item.line_item_type === 'hourly_rate' || item.line_item_type === 'salary') 
          && item.description?.includes('Holiday')
        )
        .filter(item => !item.description?.includes('Service Charge')) // Exclude service charges from taxable income
        .reduce((sum, item) => sum + item.gross_amount, 0),
      allowances: lineItems
        .filter(item => item.line_item_type === 'allowance')
        .filter(item => !item.description?.includes('Service Charge')) // Exclude service charges from taxable income
        .reduce((sum, item) => sum + item.gross_amount, 0),
      bonuses: lineItems
        .filter(item => item.line_item_type === 'bonus')
        .filter(item => !item.description?.includes('Service Charge')) // Exclude service charges from taxable income
        .reduce((sum, item) => sum + item.gross_amount, 0)
    };
    const taxableEarnings = resolveTaxableEarnings(taxableComponents);
    
    devLog('[TAX DEBUG] Taxable components breakdown:', {
      regular: taxableComponents.regular,
      overtime: taxableComponents.overtime,
      holiday: taxableComponents.holiday,
      allowances: taxableComponents.allowances,
      bonuses: taxableComponents.bonuses,
      total: taxableEarnings,
      lineItemCount: lineItems.filter(item => item.category === 'earnings').length
    });
    
    let taxSummary: TaxCalculationResult | undefined;

    // Calculate deductions (including tax)
    if (rulesByType['deduction']) {
      for (const deductionRule of rulesByType['deduction']) {
        const ruleWithCategory = deductionRule as any; // Extended type with new fields
        
        // Check if this is a tax deduction
        if (ruleWithCategory.deduction_category === 'tax' && ruleWithCategory.tax_brackets) {
          // TAX DEDUCTION: Use YTD calculation with progressive brackets
          try {
            // Extract fiscal year start month from tax config (default: January)
            const fyMonth = (ruleWithCategory.tax_brackets as any)?.fiscal_year_start_month ?? 1;
            
            // Fetch YTD data for this employee
            const ytdData = await calculationEngine.fetchYTDTaxData(
              input.profileId,
              input.payPeriodStart,
              fyMonth
            );
            
            // Normalize YTD values before calculation
            const safeYTDEarnings = toSafeNumber(ytdData.ytdTaxableEarnings, 0);
            const safeYTDWithheld = toSafeNumber(ytdData.ytdTaxWithheld, 0);
            const safeTaxableEarnings = toSafeNumber(taxableEarnings, 0);
            
            devLog('[TAX CALC] Normalized inputs:', {
              taxableEarnings: safeTaxableEarnings,
              ytdEarnings: safeYTDEarnings,
              ytdWithheld: safeYTDWithheld
            });
            
            const taxResult = calculateProgressiveTax(
              ruleWithCategory.tax_brackets as TaxBracketConfig,
              safeTaxableEarnings,
              safeYTDEarnings,
              safeYTDWithheld,
              deductionRule.id,
              deductionRule.rule_name
            );
            
            devLog('[TAX CALC] Tax result:', {
              current_period_withholding: taxResult.current_period_withholding,
              annual_tax_rounded: taxResult.annual_tax_rounded,
              ytd_taxable_income: taxResult.ytd_taxable_income,
              rounded_pkp: taxResult.rounded_pkp,
              breakdown: taxResult.breakdown
            });
            
            totalDeductions += taxResult.current_period_withholding;
            
            // Create detailed line item with breakdown
            lineItems.push({
              line_item_type: 'deduction',
              calculation_method: 'tiered_rate', // Indicate progressive
              description: `${deductionRule.rule_name} (Progressive Tax)`,
              category: 'taxes',
              gross_amount: taxResult.current_period_withholding,
              net_amount: taxResult.current_period_withholding,
              compensation_rule_id: deductionRule.id,
              calculation_notes: JSON.stringify({
                ytd_taxable: taxResult.ytd_taxable_income,
                rounded_pkp: taxResult.rounded_pkp,
                annual_tax: taxResult.annual_tax_rounded,
                ytd_withheld: taxResult.ytd_tax_withheld,
                current_period_withholding: taxResult.current_period_withholding,
                breakdown: taxResult.breakdown
              }),
              display_order: 20
            });
            
            // Store for extended result
            taxSummary = taxResult;
          } catch (error) {
            console.error('[TAX CALC] Full error details:', {
              error,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined,
              ruleName: deductionRule.rule_name,
              ruleId: deductionRule.id,
              taxableEarnings,
              hasTaxBrackets: !!ruleWithCategory.tax_brackets,
              taxBracketsType: typeof ruleWithCategory.tax_brackets,
              profileId: input.profileId,
              periodStart: input.payPeriodStart.toISOString()
            });
            
            // Add a zero deduction line item to maintain transparency
            lineItems.push({
              line_item_type: 'deduction',
              calculation_method: 'fixed_amount',
              description: `${deductionRule.rule_name} (Calculation Error - See Logs)`,
              category: 'taxes',
              gross_amount: 0,
              net_amount: 0,
              compensation_rule_id: deductionRule.id,
              calculation_notes: JSON.stringify({
                error: 'Tax calculation failed',
                message: error instanceof Error ? error.message : String(error)
              }),
              display_order: 20
            });
            
            // Continue with other deductions
          }
        } else {
          // OTHER DEDUCTION: Check for deduction_type-based calculation
          const deductionType = ruleWithCategory.deduction_type as string | null | undefined;
          let deductionAmount = 0;
          let deductionDescription = deductionRule.description || deductionRule.rule_name;
          let deductionCalcNotes: string | undefined;

          if (deductionType === 'per_pay_period') {
            // Fixed amount per pay period
            deductionAmount = Number(deductionRule.base_rate) || 0;
            deductionCalcNotes = JSON.stringify({ type: 'per_pay_period', base_rate: deductionAmount });
          } else if (deductionType === 'per_worked_day') {
            // Per worked day: base_rate × eligible days
            if (analytics && analytics.dayAnalytics) {
              const minHours = Number(ruleWithCategory.min_hours_per_day) || 0;
              const eligibleDays = analytics.dayAnalytics.filter(
                (day: any) => day.shifts && day.shifts.length > 0 && day.totalHours >= minHours
              ).length;
              deductionAmount = (Number(deductionRule.base_rate) || 0) * eligibleDays;
              deductionCalcNotes = JSON.stringify({
                type: 'per_worked_day',
                base_rate: Number(deductionRule.base_rate) || 0,
                eligible_days: eligibleDays,
                min_hours_per_day: minHours,
                total: deductionAmount
              });
            } else {
              // No analytics: produce 0 with warning
              deductionAmount = 0;
              deductionCalcNotes = JSON.stringify({
                type: 'per_worked_day',
                warning: 'Analytics data unavailable for per-worked-day deduction. Amount set to 0.',
                base_rate: Number(deductionRule.base_rate) || 0
              });
            }
          } else if (deductionType === 'percentage_of_gross') {
            // Percentage of total earnings
            const percentageRate = Number(deductionRule.percentage_rate) || 0;
            deductionAmount = totalEarnings * percentageRate / 100;
            // Apply cap: treat threshold_amount <= 0 or null as "no cap"
            const capAmount = Number(ruleWithCategory.threshold_amount) || 0;
            if (capAmount > 0) {
              deductionAmount = Math.min(deductionAmount, capAmount);
            }
            deductionCalcNotes = JSON.stringify({
              type: 'percentage_of_gross',
              percentage_rate: percentageRate,
              total_earnings: totalEarnings,
              calculated: totalEarnings * percentageRate / 100,
              cap: capAmount > 0 ? capAmount : 'none',
              final: deductionAmount
            });
          } else {
            // No deduction_type: skip with warning (legacy untyped deductions no longer supported)
            console.warn(`[DEDUCTION] Skipping deduction rule "${deductionRule.rule_name}" (${deductionRule.id}): no deduction_type set. Create a typed deduction instead.`);
            deductionAmount = 0;
          }

          if (deductionAmount > 0) {
            totalDeductions += deductionAmount;
            
            lineItems.push({
              line_item_type: 'deduction',
              calculation_method: deductionRule.calculation_method,
              description: deductionDescription,
              category: 'deductions',
              hours_applied: deductionRule.calculation_method === 'fixed_amount' ? undefined : input.hoursWorked,
              rate_applied: deductionRule.base_rate || deductionRule.percentage_rate,
              base_amount: deductionRule.base_rate || 0,
              gross_amount: deductionAmount,
              net_amount: deductionAmount,
              is_taxable: false,
              compensation_rule_id: deductionRule.id,
              calculation_notes: deductionCalcNotes,
              display_order: 20
            });
          } else if (deductionType && deductionCalcNotes) {
            // Push 0-amount line item with warning for per_worked_day no-analytics case
            lineItems.push({
              line_item_type: 'deduction',
              calculation_method: deductionRule.calculation_method,
              description: deductionDescription,
              category: 'deductions',
              gross_amount: 0,
              net_amount: 0,
              is_taxable: false,
              compensation_rule_id: deductionRule.id,
              calculation_notes: deductionCalcNotes,
              display_order: 20
            });
          }
        }
      }
    }

    // Calculate bonuses
    if (rulesByType['bonus']) {
      for (const bonusRule of rulesByType['bonus']) {
        const bonusAmount = calculationEngine.calculateRuleAmount(bonusRule, input, totalEarnings);
        if (bonusAmount > 0) {
          totalEarnings += bonusAmount;
          
          lineItems.push({
            line_item_type: 'bonus',
            calculation_method: bonusRule.calculation_method,
            description: bonusRule.description || bonusRule.rule_name,
            category: 'earnings',
            gross_amount: bonusAmount,
            net_amount: bonusAmount,
            compensation_rule_id: bonusRule.id,
            display_order: 15
          });
        }
      }
    }

    const totalGrossPay = totalEarnings;
    const postTaxNetBeforeServiceCharge = totalEarnings - totalDeductions;
    const netPay = postTaxNetBeforeServiceCharge; // Service charges will be added separately

    return {
      totalGrossPay,
      totalEarnings,
      totalDeductions,
      netPay,
      taxableEarnings, // NEW: Snapshot of taxable earnings
      taxSummary, // NEW: Tax calculation details if tax rule applied
      serviceChargeApplied: 0, // Will be added by service charge calculation
      postTaxNetBeforeServiceCharge, // NEW: Net before service charges
      lineItems: lineItems.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
      calculationSummary: {
        regularHours,
        overtimeHours: actualOvertimeHours,
        holidayHours: input.holidayHours || 0,
        paidLeaveHours: totalPaidLeaveHours,
        nonScheduledDayHours: totalNonScheduledHours,
        regularRate: effectiveRegularRate,
        overtimeRate: overtimeRate > 0 ? overtimeRate : undefined,
        holidayRate: (input.holidayHours || 0) > 0 ? holidayRate : undefined,
        rateSource: regularRateResult.source !== 'none' ? regularRateResult.source : undefined,
        annualSalary: regularRateResult.annualSalary
      }
    } as any; // Extended result with tax fields
  },

  /**
   * Fetch year-to-date tax data for a profile
   * Used for progressive tax calculation based on YTD earnings
   */
  fetchYTDTaxData: async (
    profileId: string,
    periodStart: Date,
    fiscalYearStartMonth: number = 1 // 1-12, default January
  ): Promise<{ ytdTaxableEarnings: number; ytdTaxWithheld: number }> => {
    // Derive fiscal year start using configurable month
    const periodMonth = periodStart.getMonth() + 1; // 1-indexed
    const periodYear = periodStart.getFullYear();
    const safeMonth = fiscalYearStartMonth >= 1 && fiscalYearStartMonth <= 12 ? fiscalYearStartMonth : 1;
    
    // If pay period month is before fiscal start month, fiscal year started last calendar year
    const fiscalYear = periodMonth < safeMonth ? periodYear - 1 : periodYear;
    const fiscalYearStart = new Date(fiscalYear, safeMonth - 1, 1); // months are 0-indexed in Date constructor
    
    devLog('[YTD] Query parameters:', {
      profileId,
      fiscalYearStartMonth: safeMonth,
      fiscalYearStart: fiscalYearStart.toISOString(),
      periodStart: periodStart.toISOString(),
      dateFilter: {
        gte: format(fiscalYearStart, 'yyyy-MM-dd'),
        lt: format(periodStart, 'yyyy-MM-dd')
      }
    });
    
    try {
      // Query payslips table for YTD totals - ONLY from completed payroll runs
      const { data: ytdPayslips, error } = await supabase
        .from('payslips')
        .select(`
          start_date, 
          end_date, 
          taxable_earnings, 
          tax_withheld,
          payroll_runs!inner (
            status
          )
        `)
        .eq('profile_id', profileId)
        .eq('payroll_runs.status', 'completed')
        .gte('start_date', format(fiscalYearStart, 'yyyy-MM-dd'))
        .lt('start_date', format(periodStart, 'yyyy-MM-dd'));
      
      if (error) {
        console.error('[YTD] Error fetching tax data:', error);
        return { ytdTaxableEarnings: 0, ytdTaxWithheld: 0 };
      }
      
      devLog('[YTD] Raw database results:', {
        recordCount: ytdPayslips?.length || 0,
        rawRecords: ytdPayslips?.map(p => ({
          start_date: p.start_date,
          end_date: p.end_date,
          taxable_earnings: p.taxable_earnings,
          tax_withheld: p.tax_withheld,
          types: {
            taxable_earnings: typeof p.taxable_earnings,
            tax_withheld: typeof p.tax_withheld
          }
        }))
      });
      
      // Normalize each value before summing (critical for Supabase numeric types)
      const ytdTaxableEarnings = ytdPayslips?.reduce((sum, p) => {
        const earnings = toSafeNumber(p.taxable_earnings, 0);
        devLog('[YTD] Processing taxable earnings:', {
          raw: p.taxable_earnings,
          normalized: earnings,
          runningSum: sum + earnings
        });
        return sum + earnings;
      }, 0) ?? 0;
      
      const ytdTaxWithheld = ytdPayslips?.reduce((sum, p) => {
        const withheld = toSafeNumber(p.tax_withheld, 0);
        devLog('[YTD] Processing tax withheld:', {
          raw: p.tax_withheld,
          normalized: withheld,
          runningSum: sum + withheld
        });
        return sum + withheld;
      }, 0) ?? 0;
      
      devLog('[YTD] Final normalized totals:', {
        profileId,
        recordCount: ytdPayslips?.length || 0,
        ytdTaxableEarnings,
        ytdTaxWithheld
      });
      
      return { ytdTaxableEarnings, ytdTaxWithheld };
    } catch (error) {
      console.error('Exception fetching YTD tax data:', error);
      return { ytdTaxableEarnings: 0, ytdTaxWithheld: 0 };
    }
  },

  /**
   * Get the effective hourly rate from compensation rules with source metadata
   * Supports both hourly_rate and salary rules with contract-aware conversion
   * Priority: hourly_rate > salary (converted)
   * @param ctx - Optional rate context for salary-to-hourly conversion
   * @param baseRate - Optional base rate for multiplier calculations
   * @returns RateResult with rate, source, ruleId, and metadata for traceability
   */
  getEffectiveRate: (
    hourlyRules: CompensationRule[], 
    salaryRules?: CompensationRule[],
    ctx: RateContext = {},
    baseRate?: number // ✅ NEW: For multiplier calculations
  ): RateResult => {
    // Priority 1: Hourly rate rules (including multiplier-based overtime)
    if (hourlyRules && hourlyRules.length > 0) {
      const activeRule = hourlyRules
        .filter(rule => rule.is_active)
        .sort((a, b) => a.priority_order - b.priority_order)[0];
      
      if (activeRule) {
        // ✅ NEW: Handle multiplier calculation method
        if (activeRule.calculation_method === 'multiplier' && baseRate !== undefined) {
          const multiplier = Number(activeRule.multiplier_rate) || 1.5;
          return {
            rate: baseRate * multiplier,
            source: 'hourly_rate',
            ruleId: activeRule.id,
            metadata: { multiplier } // ✅ Track multiplier for transparency
          };
        }
        
        // Existing fixed_amount logic
        return {
          rate: Number(activeRule.base_rate) || 0,
          source: 'hourly_rate',
          ruleId: activeRule.id
        };
      }
    }
    
    // Priority 2: Convert salary to hourly rate via context
    if (salaryRules && salaryRules.length > 0) {
      const activeRule = salaryRules
        .filter(rule => rule.is_active)
        .sort((a, b) => a.priority_order - b.priority_order)[0];
      
      if (activeRule) {
        // Build context with rule's weekly_hours override
        const contextWithOverride: RateContext = {
          ...ctx,
          salaryRuleWeeklyHours: activeRule.weekly_hours ?? ctx.salaryRuleWeeklyHours
        };
        
        const salaryMode = (activeRule as any).salary_mode || 'annual';
        const rate = calculationEngine.convertSalaryToHourlyRate(activeRule, contextWithOverride);
        const weeklyHours = resolveWeeklyHours(contextWithOverride);
        
        const result: RateResult = {
          rate,
          source: 'salary',
          ruleId: activeRule.id,
          annualSalary: Number(activeRule.base_rate),
          weeklyHours,
          salaryMode,
        };
        
        // For monthly_fixed mode, include the fixed amount for base pay calculation
        if (salaryMode === 'monthly_fixed') {
          result.monthlyFixedAmount = Number(activeRule.base_rate) || 0;
        }
        
        return result;
      }
    }
    
    return { rate: 0, source: 'none' };
  },

  /**
   * Convert annual salary to equivalent hourly rate using context-aware weekly hours
   * Supports employee contracts, rule overrides, and company defaults
   * @param salaryRule - Salary compensation rule (may contain weekly_hours override)
   * @param ctx - Rate context for flexible hours resolution (backward compatible)
   * @returns Equivalent hourly rate
   */
  convertSalaryToHourlyRate: (
    salaryRule: CompensationRule, 
    ctx: RateContext = {}
  ): number => {
    const baseAmount = Number(salaryRule.base_rate) || 0;
    if (baseAmount <= 0) return 0;
    
    // Build context with rule's weekly_hours override if present
    const contextWithRuleOverride: RateContext = {
      ...ctx,
      salaryRuleWeeklyHours: salaryRule.weekly_hours ?? ctx.salaryRuleWeeklyHours
    };
    
    const weeksPerYear = ctx.weeksPerYear ?? 52;
    const weeklyHours = resolveWeeklyHours(contextWithRuleOverride);
    
    const salaryMode = (salaryRule as any).salary_mode || 'annual';
    
    if (salaryMode === 'monthly_fixed') {
      // Monthly fixed: derive hourly rate for OT/holiday/leave calculations only
      // monthlyAmount / (weeklyHours × weeksPerMonth)
      const weeksPerMonth = weeksPerYear / 12;
      const monthlyHours = weeklyHours * weeksPerMonth;
      return monthlyHours > 0 ? baseAmount / monthlyHours : 0;
    }
    
    // Annual mode (default): annualSalary / totalAnnualHours
    const totalAnnualHours = weeklyHours * weeksPerYear;
    return totalAnnualHours > 0 ? baseAmount / totalAnnualHours : 0;
  },

  /**
   * Calculate amount for a specific rule based on its calculation method.
   * @param totalEarnings - current accumulated earnings, required for percentage mode
   */
  calculateRuleAmount: (
    rule: CompensationRule, 
    input: CompensationCalculationInput, 
    totalEarnings: number = 0
  ): number => {
    switch (rule.calculation_method) {
      case 'fixed_amount':
        return Number(rule.base_rate) || 0;
        
      case 'percentage': {
        const percentageRate = Number(rule.percentage_rate) || 0;
        if (percentageRate <= 0) {
          console.warn(`[calculateRuleAmount] percentage rule "${rule.rule_name}" has no percentage_rate configured`);
          return 0;
        }
        if (totalEarnings <= 0) {
          console.warn(`[calculateRuleAmount] percentage rule "${rule.rule_name}" applied to zero totalEarnings`);
        }
        return totalEarnings * percentageRate / 100;
      }
        
      case 'tiered_rate':
        // tiered_rate is only supported for overtime (handled by calculateTieredOvertimeAmount).
        // Fail-fast for any other rule type to prevent silent miscalculation.
        throw new Error(
          `[calculateRuleAmount] tiered_rate is not supported for rule type "${rule.rule_type}" ` +
          `(rule: "${rule.rule_name}"). Use calculateTieredOvertimeAmount for overtime tiers.`
        );
        
      case 'multiplier': {
        const baseAmount = Number(rule.base_rate) || 0;
        const multiplier = Number(rule.multiplier_rate) || 1.0;
        return baseAmount * multiplier;
      }
        
      default:
        throw new Error(
          `[calculateRuleAmount] Unknown calculation_method "${rule.calculation_method}" ` +
          `on rule "${rule.rule_name}". Refusing to return $0 silently.`
        );
    }
  },

  /**
   * Calculate tiered overtime amount
   */
  calculateTieredOvertimeAmount: (
    overtimeHours: number, 
    overtimeRule: CompensationRule, 
    baseRate: number,
    currencyCode?: string
  ): import('./types').TieredOvertimeCalculation => {
    const tiers = (overtimeRule.tiered_rates as unknown as import('./types').OvertimeTier[]) || [];
    const currencySymbol = getCurrencySymbol(currencyCode);
    const tierBreakdown: Array<{
      tier: number;
      hours: number;
      rate: number;
      amount: number;
      description: string;
    }> = [];
    
    let remainingHours = overtimeHours;
    let totalAmount = 0;
    
    // Sort tiers by from_hours to ensure correct processing order
    const sortedTiers = [...tiers].sort((a, b) => a.from_hours - b.from_hours);
    
    for (let i = 0; i < sortedTiers.length && remainingHours > 0; i++) {
      const tier = sortedTiers[i];
      const tierStart = tier.from_hours;
      const tierEnd = tier.to_hours;
      
      // Calculate hours for this tier
      let tierHours: number;
      if (tierEnd === null) {
        // Unlimited tier - use all remaining hours
        tierHours = remainingHours;
      } else {
        // Limited tier - use hours within the tier range
        const tierMaxHours = tierEnd - tierStart;
        tierHours = Math.min(remainingHours, tierMaxHours);
      }
      
      if (tierHours > 0) {
        const tierRate = baseRate * tier.multiplier;
        const tierAmount = tierHours * tierRate;
        
        tierBreakdown.push({
          tier: i + 1,
          hours: tierHours,
          rate: tierRate,
          amount: tierAmount,
          description: `Overtime Tier ${i + 1} (${tierHours.toFixed(2)} hrs @ ${currencySymbol}${tierRate.toFixed(2)}/hr - ${tier.multiplier}x base rate)`
        });
        
        totalAmount += tierAmount;
        remainingHours -= tierHours;
      }
    }
    
    return {
      totalAmount,
      tierBreakdown
    };
  },

  /**
   * Calculate overtime based on configurable thresholds
   * @param dailyThreshold - Hours threshold for daily overtime (default: 8)
   * @param weeklyThreshold - Hours threshold for weekly overtime (default: 40)
   */
  calculateOvertimeHours: (
    dailyHours: number[], 
    weeklyHours: number,
    dailyThreshold: number = 8,
    weeklyThreshold: number = 40
  ): { dailyOvertime: number; weeklyOvertime: number } => {
    // Daily overtime (configurable threshold)
    const dailyOvertime = dailyHours.reduce((total, hours) => {
      return total + Math.max(0, hours - dailyThreshold);
    }, 0);

    // Weekly overtime (configurable threshold, excluding daily overtime)
    const regularWeeklyHours = weeklyHours - dailyOvertime;
    const weeklyOvertime = Math.max(0, regularWeeklyHours - weeklyThreshold);

    return {
      dailyOvertime,
      weeklyOvertime
    };
  }
};
